// proposalRules.js
// -----------------------------------------------------------------------------
// Propose and confirm: a shop user who answers a customer about SOMEONE ELSE'S
// booking -- at night, on a holiday -- reserves a new slot (or asks for a
// cancel), and the owner, a supervisor of the owner's group, or staff decides
// next morning. The proposal lives on the bookingSlot document; a move's held
// seat is an ordinary count on the target bookingDay, so every availability
// reader already sees it.
//
// Pure -- no Firestore, no Vue -- asserted by `npm run test:proposals`.
// See docs/superpowers/specs/2026-09-21-propose-and-confirm-design.md.
//
// Like every booking rule, this is a UI rule, not a security boundary:
// Firestore rules are open on bookingSlot.
// -----------------------------------------------------------------------------
const R = require('./bookingRules')
const P = require('./people')
const RJ = require('./returnJob')

const PENDING = 'pending'
const REJECTED_UNTOLD = 'rejectedUntold'

function fail (reason) { return { ok: false, reason: reason } }

function isPending (b) {
  return !!b && b.proposalStatus === PENDING && !!b.proposal
}

// May this user propose on this booking? Staff and the owner act directly, so
// they never propose. A dispatched job belongs to delivery. A planned one may
// take a proposal, but whoever confirms it has to re-plan.
function checkProposal (b, ctx) {
  const c = ctx || {}
  if (!b || !R.isActive(b.status)) return fail('ALREADY_CANCELLED')
  if (R.isStaffRole(c.role)) return fail('STAFF_ACTS_DIRECTLY')
  if (P.ownedBy(b.user, c.identities)) return fail('OWN_BOOKING')
  if (isPending(b)) return fail('PROPOSAL_PENDING')
  if (c.dispatched) return fail('DISPATCHED')
  return b.planVersion != null ? { ok: true, warn: 'PLANNED' } : { ok: true }
}

// The seat a move proposal will hold, judged as a shop's own move is judged
// (bookingService.moveBooking's owner branch) with one difference: the booking
// is NOT removed from the day first, because a hold does not free the old seat
// until someone confirms. `day` is the target bookingDay as it stands.
function checkProposalTarget (b, t) {
  if (R.SLOTS.indexOf(t.slot) === -1) return fail('BAD_SLOT')
  if (b.date === t.date && b.slot === t.slot) return fail('SAME_SLOT')
  if (R.isClosedSunday(t.date, t.day)) return fail('SUNDAY')
  if (t.date < t.today) return fail('PAST')
  const orderDate = b.orderDate || String(b.createdAt || '').slice(0, 10) || t.today
  if (t.date < R.earliestDeliveryDate(orderDate, t.leadDays)) return fail('TOO_EARLY')
  if (t.date > R.horizonDate(t.today, t.horizonDays)) return fail('TOO_LATE')
  const sameDay = b.date === t.date
  const open = R.checkDayOpen(t.day, t.role, { outOfArea: b.outOfArea, sameDay: sameDay })
  if (!open.ok) return open
  if (!b.outOfArea) {
    const cap = R.checkCapacity(t.day, t.caps, t.slot)
    if (!cap.ok) return cap
    return { ok: true }
  }
  if (!t.toId) return fail('NO_TRIP')
  if (!t.sameTrip) {
    const seat = R.checkTripSeat(t.toTrip)
    if (!seat.ok) return seat
  }
  return { ok: true }
}

// The userProfile an identity belongs to: its uid, current email, or any email
// it has had -- a booking keeps the email it was made with.
function profileOf (profiles, identity) {
  if (!identity) return null
  return (profiles || []).find(p => p && (p.uid === identity ||
    P.identitiesOf(p, p.email).indexOf(identity) !== -1)) || null
}

// Staff; the owner; or a supervisor of the owner's group. An owner in no group
// can be decided for only by themselves or staff -- which is every booking
// until an administrator assigns groups.
function canDecide (b, ctx) {
  const c = ctx || {}
  if (R.isStaffRole(c.role)) return true
  // A proposer never decides their own proposal. checkProposal already refuses
  // OWN_BOOKING, so this never fires against an owner deciding their own
  // booking -- it exists for a group supervisor, who can propose on a member's
  // booking and would otherwise also qualify to decide it.
  if (b && b.proposal && P.ownedBy(b.proposal.by, c.identities)) return false
  if (P.ownedBy(b && b.user, c.identities)) return true
  return supervises(c)
}

// Is the caller a supervisor of the booking owner's group? `x` carries both
// profiles already resolved (ctxFor). An owner in no group has no supervisor.
function supervises (x) {
  const group = x && x.ownerProfile && x.ownerProfile.bookingGroup
  return !!group && !!x.callerProfile && x.callerProfile.supervisor === true &&
    x.callerProfile.bookingGroup === group
}

// ใส่ SO on a นัดหมาย: the owner, staff, or a supervisor of the owner's group.
// The supervisor case is for a group whose PCs book without an SO while head
// office creates it in SAP -- the head fills the number in for the whole group.
// It grants ใส่ SO alone: ย้ายวัน / ยกเลิก / แก้ไข stay the owner's and staff's
// (a supervisor proposes those like any other shop). `c` is
// { role, identities, user, profiles }, the same shape panelRows takes.
function canAttachSo (b, c) {
  if (!b || !R.isActive(b.status) || b.soNo || !RJ.isAppointment(b)) return false
  const ctx = c || {}
  if (ctx.role === 'driver') return false
  if (R.isStaffRole(ctx.role)) return true
  if (P.ownedBy(b.user, ctx.identities)) return true
  return supervises(ctxFor(b, ctx))
}

// เพิ่ม RT ในการจองนี้ (an exchange found from the RT side): the same people as
// ใส่ SO -- owner, staff, or a supervisor of the owner's group -- on a booking
// that already has its SO. Taking no seat, it needs no more than that.
function canAttachRt (b, c) {
  if (!b || !R.isActive(b.status) || !b.soNo || b.rtNo) return false
  const ctx = c || {}
  if (ctx.role === 'driver') return false
  if (R.isStaffRole(ctx.role)) return true
  if (P.ownedBy(b.user, ctx.identities)) return true
  return supervises(ctxFor(b, ctx))
}

function canWithdraw (b, ctx) {
  if (!isPending(b)) return false
  return P.ownedBy(b.proposal.by, (ctx || {}).identities) || canDecide(b, ctx)
}

// Whoever can phone the customer about a rejection: who proposed it, who
// rejected it, or anyone who could have decided it.
function canMarkTold (b, ctx) {
  if (!b || b.proposalStatus !== REJECTED_UNTOLD || !b.proposal) return false
  const ids = (ctx || {}).identities
  return P.ownedBy(b.proposal.by, ids) || P.ownedBy(b.proposal.decidedBy, ids) || canDecide(b, ctx)
}

function newProposal (o) {
  const move = o.kind === 'move'
  return {
    kind: o.kind,
    date: move ? o.date : '',
    slot: move ? o.slot : '',
    tripId: move ? (o.tripId || null) : null,
    holdsTripSeat: move ? !!o.holdsTripSeat : false,
    reason: o.reason || '',
    note: o.note || '',
    by: o.by || '',
    at: o.at,
    planned: !!o.planned,
    status: PENDING,
    decidedBy: '',
    decidedAt: '',
    rejectReason: '',
    customerTold: false
  }
}

// A new call on a booking whose last proposal was rejected and never phoned
// about: the new caller IS talking to the customer, so that one closes as told.
function proposePatch (b, proposal) {
  const history = (b.proposalHistory || []).slice()
  if (b.proposalStatus === REJECTED_UNTOLD && b.proposal) {
    history.push(Object.assign({}, b.proposal, { customerTold: true, toldBy: proposal.by, toldAt: proposal.at }))
  }
  return { proposal: proposal, proposalStatus: PENDING, proposalHistory: history }
}

// `untold` keeps a rejection open until someone has phoned the customer.
function decisionPatch (b, fields, untold) {
  const closed = Object.assign({}, b.proposal, fields)
  if (untold) return { proposal: closed, proposalStatus: REJECTED_UNTOLD }
  return { proposal: null, proposalStatus: null, proposalHistory: (b.proposalHistory || []).concat([closed]) }
}

// What a booking contributes to bookingDay counters: its own seat, plus the
// seat a pending move holds. recountRange must use this, or a recount would
// silently hand a held seat to someone else.
function countsForRecount (b) {
  if (!b || !R.isActive(b.status)) return []
  const out = [{ date: b.date, slot: b.slot, outOfArea: !!b.outOfArea }]
  if (isPending(b) && b.proposal.kind === 'move') {
    out.push({ date: b.proposal.date, slot: b.proposal.slot, outOfArea: !!b.outOfArea })
  }
  return out
}

// The pending proposals that must be decided before `date` is committed on
// dispatchPlan: the ones moving off it, onto it, or cancelling on it.
function proposalBlocksPlan (rows, date) {
  // A large-truck order is on no small-car day: its cancel request cannot touch a plan.
  return (rows || []).filter(b => b.fleet !== 'large' && isPending(b) &&
    (b.date === date || (b.proposal.kind === 'move' && b.proposal.date === date)))
}

// Expiry is read, never written: a pending move whose target day has passed
// holds a seat on a day that is gone, so nothing is lost by leaving it until
// someone closes it.
function proposalState (b, today) {
  if (!b) return null
  if (b.proposalStatus === REJECTED_UNTOLD && b.proposal) return REJECTED_UNTOLD
  if (!isPending(b)) return null
  if (b.proposal.kind === 'move' && b.proposal.date < today) return 'expired'
  return PENDING
}

function ctxFor (b, c) {
  return {
    role: c.role,
    identities: c.identities,
    callerProfile: profileOf(c.profiles, c.user),
    ownerProfile: profileOf(c.profiles, b.user)
  }
}

// What this user sees on คำขอรอยืนยัน: what they may decide, what they proposed,
// and rejections they may phone about. Oldest first -- the customer who has
// waited longest.
function panelRows (rows, c) {
  const out = []
  ;(rows || []).forEach(b => {
    const state = proposalState(b, c.today)
    if (!state) return
    const x = ctxFor(b, c)
    const decide = state !== REJECTED_UNTOLD && canDecide(b, x)
    const withdraw = state !== REJECTED_UNTOLD && canWithdraw(b, x)
    const told = canMarkTold(b, x)
    if (decide || withdraw || told) out.push({ booking: b, state: state, decide: decide, withdraw: withdraw, told: told })
  })
  return out.sort((a, b) => String(a.booking.proposal.at).localeCompare(String(b.booking.proposal.at)))
}

// The number on the จองวันส่งสินค้า badge: work waiting on THIS user. Their own
// pending proposals wait on someone else and are not counted.
function badgeCount (rows, c) {
  if (c.role === 'driver') return 0
  return panelRows(rows, c).filter(r => r.decide || r.told).length
}

// The short word a refused chip shows in the ขอเลื่อน dialog. The full Thai
// sentence is still BOOKING_ERROR_TH's, for the save that is refused.
const CHIP_LABEL = {
  SAME_SLOT: 'นัดเดิม',
  DAY_FULL: 'เต็ม',
  AM_FULL: 'เต็ม',
  PM_FULL: 'เต็ม',
  DAY_STOPPED: 'ปิดรับ',
  SUNDAY: 'วันอาทิตย์',
  PAST: 'ผ่านแล้ว',
  TOO_EARLY: 'ยังไม่ถึงกำหนด',
  TOO_LATE: 'เกินกำหนด',
  NO_TRIP: 'ไม่มีเส้นทาง',
  TRIP_FULL: 'เส้นทางเต็ม',
  TRIP_CLOSED_ERR: 'เส้นทางปิดรับ',
  BAD_SLOT: 'ไม่ถูกต้อง'
}

// Seats a slot still has. ทั้งวัน draws on the day total only, as it does in
// checkCapacity; เช้า / บ่าย are bounded by their own cap and the day's.
function seatsLeft (counts, caps, slot) {
  const c = Object.assign({ am: 0, pm: 0, fullDay: 0, total: 0 }, counts || {})
  const day = (caps.total || 0) - c.total
  if (slot === 'full') return { left: Math.max(0, day), cap: caps.total || 0 }
  return { left: Math.max(0, Math.min((caps[slot] || 0) - c[slot], day)), cap: caps[slot] || 0 }
}

// The chips the ขอเลื่อน dialog draws for one day: one per slot, each judged by
// checkProposalTarget -- the rule proposeMove applies on save -- so the grid
// never offers a slot the save would refuse. `day` is the bookingDay document
// (or null); `ctx` is { date, role, today, leadDays, horizonDays, caps }, caps
// already reduced by any truck an out-of-area route holds (R.capacityForDay).
//
// An out-of-area booking takes a seat on the day's route to its province, so
// its chips count that route's seats, and the day's mirror entry stands in for
// the trip document (a full route reads as full, not as closed by hand).
//
// -> [{ slot, ok, reason, label, current, left, cap }]
function slotChoices (b, day, ctx) {
  const c = ctx || {}
  const counts = day || {}
  const entry = b.outOfArea ? ((counts.tripByProvince || {})[R.provinceOf(b)] || null) : null
  const toId = entry && entry.tripId ? entry.tripId : null
  const toTrip = entry ? Object.assign({}, entry, { autoClosed: R.tripStateOf(entry) === 'full' }) : null
  return R.SLOTS.map(slot => {
    const check = checkProposalTarget(b, {
      date: c.date, slot, role: c.role, today: c.today, leadDays: c.leadDays, horizonDays: c.horizonDays,
      day: counts, caps: c.caps, toId, sameTrip: !!toId && toId === b.tripId, toTrip
    })
    const seats = b.outOfArea
      ? { left: R.tripSeatsLeft(entry), cap: (entry && entry.capacity) || 0 }
      : seatsLeft(counts, c.caps, slot)
    const reason = check.ok ? '' : check.reason
    return {
      slot,
      ok: check.ok,
      reason,
      label: reason ? (CHIP_LABEL[reason] || reason) : '',
      current: b.date === c.date && b.slot === slot,
      left: seats.left,
      cap: seats.cap
    }
  })
}

module.exports = {
  CHIP_LABEL, slotChoices,
  PENDING, REJECTED_UNTOLD, isPending, checkProposal, checkProposalTarget, profileOf,
  canDecide, supervises, canAttachSo, canAttachRt, canWithdraw, canMarkTold, newProposal, proposePatch, decisionPatch,
  countsForRecount, proposalBlocksPlan, proposalState, panelRows, badgeCount
}
